import {DomainMediaQuery, useDomainMediaQuery} from "@/queries.generated";
import {removeFileExtension} from "@/tools";
import {ErrorWithContext, MultipleLineError} from "@dvrt/graphql-widgets";
import {Autocomplete, FormControl, TextField} from "@mui/material";
import {ChangeEvent, useRef} from "react";
import {useIntl} from "react-intl";
import {makeStyles} from "tss-react/mui";

type IdAndDescription = {
    id: number;
    description: string;
};

type MediaItem = DomainMediaQuery["media"]["items"][number];

export type DomainMediaSelectorOnChange = (value: IdAndDescription | File | undefined) => void;

type DomainMediaSelectorProps = {
    domainId: number;
    value: number | File | undefined;
    onChange: DomainMediaSelectorOnChange;
    errors?: ErrorWithContext[];
};

const useDomainMediaStyles = makeStyles()(() => ({
    paper: {
        maxHeight: "400px",
    },
}));

const DomainMediaSelector = ({domainId, value, onChange, errors}: DomainMediaSelectorProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const intl = useIntl();
    const domainMediaQuery = useDomainMediaQuery({
        variables: {domainId},
    });
    const selectValue = value instanceof File ? 0 : value;
    const isError = errors !== undefined && errors.length > 0;

    let items: DomainMediaQuery["media"]["items"] = [
        {
            id: -1,
            description: "upload_new_audio",
            mimeType: "",
            domainId,
        } as MediaItem,
    ];

    if (value instanceof File) {
        items = items.concat({
            id: 0,
            description: removeFileExtension(value.name),
            mimeType: "",
            domainId,
        } as MediaItem);
    }

    if (domainMediaQuery.data?.media.items) {
        items = items.concat(domainMediaQuery.data.media.items);
    }

    const {classes} = useDomainMediaStyles();

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file) {
            return onChange(file);
        }

        if (inputRef.current) {
            inputRef.current.value = null;
        }
    };

    const handleAutocompleteOnChange = (newValue: string | MediaItem) => {
        if (typeof newValue === "string") {
            return;
        }

        if (newValue.id === -1) {
            inputRef.current?.click();
        } else if (newValue.id === 0) {
            return;
        } else {
            onChange({
                id: newValue.id,
                description: newValue.description,
            });
        }
    };

    return (
        <FormControl sx={{minWidth: "300px"}} error={isError}>
            <input ref={inputRef} type="file" accept="audio/*" hidden onChange={handleFileChange} />
            <Autocomplete
                disablePortal
                disableClearable
                id="domain-media-selector"
                options={items}
                getOptionLabel={(option) => {
                    if (typeof option === "string") {
                        return option;
                    }

                    if (option.id === -1) {
                        return intl.formatMessage({id: "upload_new_audio", defaultMessage: "Upload new audio"});
                    }

                    return option.description;
                }}
                value={items.find((item) => item.id === selectValue) ?? ""}
                onChange={(_, newValue) => handleAutocompleteOnChange(newValue)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={intl.formatMessage({id: "audio", defaultMessage: "Audio"})}
                        error={isError}
                        helperText={<MultipleLineError errors={errors} />}
                    />
                )}
                classes={{paper: classes.paper}}
            />
        </FormControl>
    );
};

export default DomainMediaSelector;
